import { Factory } from "@/data/dao/Factory";
import { DocumentDao } from "@/data/dao/service/DocumentDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import type { IDocument } from "@/data/model/Document";
import type { IDocumentsQry } from "@/data/queries/document/DocumentsQry";
import type { IFileInfo } from "@/data/model/System";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";
import { Misc } from "@/data/util/Misc";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";
import { TastierGlobal } from "@/portal/globalAccess";
import { DocumentScheduleWindow } from "@/portal/modal/DocumentScheduleWindow";

export class DocumentTableController {
    private $element: JQuery;
    private dao: DocumentDao;
    private dataTable: DataTable<IDocument>;
    private search: TableFilter;

    private scheduleWindow?: DocumentScheduleWindow;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<DocumentDao>(DocumentDao);

        const tableConfig: IDataTableConfig<IDocument> = {
            columnMapping: [
                {
                    name: i18n("general.fields.internalName"),
                    property: "name",
                    styles: { width: "22%" },
                },
                {
                    name: i18n("general.fields.sortOrder"),
                    property: "order",
                    styles: { width: "12%" },
                    formatter: (m, p) => (p === undefined ? i18n("general.alphabeticallyOrdered") : p),
                },
                {
                    name: i18n("documents.fields.isPublished"),
                    property: "isPublished",
                    styles: { width: "12%" },
                    formatter: (m, p) => (p ? i18n("general.yes") : i18n("general.no")),
                },
                {
                    name: i18n("documents.fields.file"),
                    property: "fileName",
                    styles: { width: "24%" },
                    formatter: (m) => {
                        const f = Format.translateProperty<IFileInfo>(m, "file", TastierGlobal.getLang());
                        if (f === undefined) return "";

                        return $(
                            '<a href="' +
                                f.path +
                                '" target="_blank">' +
                                Format.htmlEscape(f.name) +
                                "- " +
                                Misc.byteToReadableString(f.size) +
                                "</a>"
                        ).click((e) => {
                            e.preventDefault();
                        });
                    },
                },
                {
                    name: i18n("general.fields.created"),
                    property: "created",
                    styles: { width: "13%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("documents.fields.modified"),
                    property: "modified",
                    styles: { width: "13%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            filterProvider: {
                getFilter: (): IDocumentsQry => ({ ...this.search.getValue(), language: TastierGlobal.getLang() }),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: true,
            locationDependend: true,
            defaultSortProperty: "order",
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilter(
            "roomId",
            Factory.getInstance<RoomDao>(RoomDao),
            true,
            i18n("documents.allRooms")
        );
        this.dataTable = new DataTable<IDocument>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = "/document/" + m.id;
            },
            (m) => {
                window.open("/document/" + m.id);
            }
        );

        this.dataTable.listenTo(this.search);
        portalEvents.OnLocationChanged.add(async () => {
            await this.search.refreshDropdownFilter("roomId");
            this.dataTable.loadData(true);
        }, this);

        element.find("#previewbtn").click((e) => {
            e.preventDefault();
            this.showPlanningPreview();
        });
    }

    private showPlanningPreview() {
        if (this.scheduleWindow == undefined) this.scheduleWindow = new DocumentScheduleWindow();

        this.scheduleWindow.show();
    }
}
